import createPictureImageSources from "utils/createPictureImageSources";

export default class BasicCommunityCard {
/**
 * Generates and sets the big image sources for different breakpoints.
 * Creates picture element sources with specified dimensions and parameters for responsive images.
 * Falls back to errorImage if thumbnail is not available.
 * 
 */
  getBigImageSources() {
    const { sources, src } = createPictureImageSources({
      url: this.thumbnail || this.errorImage,
      breakpoints: {
        '1.5x': {
          width: 447,
          height: 297
        },
        '2x': {
          width: 400,
          height: 312
        },
        '3x': {
          width: 900,
          height: 600
        }
      },
      irsParameters: {
        mode: 'crop',
        progressive: true
      }
    });

    this.bigSources = sources;
    this.src = src;
  }

  /**
   * Generates and sets the small image sources for different breakpoints.
   * Creates picture element sources with specified dimensions and parameters for responsive images.
   * Falls back to errorImage if thumbnail is not available.
   * 
   */
  getSmallImageSources() {
    const { sources, src } = createPictureImageSources({
      url: this.thumbnail || this.errorImage,
      breakpoints: {
        '230w': {
          maxwidth: 230,
          maxheight: 200
        },
        '434w': {
          maxwidth: 434,
          maxheight: 334
        },
        '541w': {
          maxwidth: 541,
          maxheight: 441
        },
        '632w': {
          maxwidth: 632,
          maxheight: 532
        },
        '716w': {
          maxwidth: 716,
          maxheight: 614
        }
      },
      irsParameters: {
        progressive: true
      }
    });

    this.smallSources = sources;
    this.src = src;
  }

  /**
   * Sets the metadata for the basic community card component.
   * Initializes card properties with provided metadata values and triggers image source generation.
   */
  setMetadata(metadata) {
    this.thumbnail = metadata?.thumbnail;
    this.communityName = metadata?.communityName;
    this.priceText = metadata?.priceText;
    this.bedrooms = metadata?.bedrooms;
    this.bathrooms = metadata?.bathrooms;
    this.address = metadata?.address;
    this.brandName = metadata?.brandName;
    this.squareFeet = metadata?.squareFeet;
    this.id = metadata?.id;
    this.url = metadata?.url;
    this.builderId = metadata?.builderId;
    this.isFeaturedListing = metadata?.isFeaturedListing;
    this.isAdult = metadata?.isAdult;
    this.communityLabel = this.isAdult ? '55+ Community' : 'Community';
    this.errorImage = null;
    this.city = metadata?.city;
    this.stateAbbreviation = metadata?.stateAbbreviation;
    this.zip = metadata?.zip;
    this.priceLow = metadata?.priceLow;
    this.priceHigh = metadata?.priceHigh;
    this.communityStatus = metadata?.communityStatus;

    this.getBigImageSources();
    this.getSmallImageSources();
  }

  /**
   * Renders a responsive picture element with multiple source formats for different screen sizes.
   */
  renderPicture() {
    return `
      <picture class="nhs-n1-c-card__image nhs-c-image--ratio-16-9">

        ${this.smallSources
          .map(
            source =>
              `<source media="(max-width: 767px)" srcset="${source.srcset}" type="${source.format}"></source>`
          )
          .join('')}

          
        ${this.bigSources
          .map(
            source =>
              `<source media="(min-width: 768px)" srcset="${source.srcset}" type="${source.format}"></source>`
          )
          .join('')}

        <img src="${this.src}"
            class="nhs-c-image--ratio-16-9"
            alt="${this.communityName} - ${this.address}"
            loading="eager"
            onerror="document.dispatchEvent(
              new CustomEvent('image-load-error', {
                bubbles: true,
                detail: { image: this }
              })
            )"
            onload="this.dataset.load=true">
      </picture>
    `;
  }

  /**
   * Generates the HTML markup for the lead generator "Learn More" button for the card.
   *
   * @returns {string} HTML string containing the "Learn More" lead generator button.
   */
  getCardButton() {
    return `
      <button
        class="nhs-c-button--primary-lead nhs-u-margin--top-auto nhs-c-button--small-32 nhs-c-button--text-only"
        data-button-action="Learn More"
        data-url="${this.url}"
        data-variation="basic"
        data-from-page="CommunityResults.MapCard.BasicCommunity"
        data-lead-generator-opener="get-information-basic">
        Learn more
      </button>
    `;
  }

/**
 * Generates the HTML markup for a basic community card component.
 * 
 * The card displays property information including images, pricing, specifications (bedrooms, bathrooms),
 * location details, and interactive elements such as favorite buttons.
 */
  getHtml() {
    const classNames = ['nhs-n1-c-card--housing', 'nhs-n1-c-card--paidlisting', 'nhs-n1-c-card--surface'];
    if (this.isFeaturedListing) {
      classNames.push('nhs-n1-c-card--spotlight');
    }
    return `
      <div
        id="${this.id}"
        class="nhs-c-tooltip--map nhs-c-tooltip--card"
        data-standard-card
        data-card
        data-map-card
        data-id="${this.id}"
        data-type="community"
        data-basic-community
        data-is-basic="true"
        data-community-id="${this.id}"
        data-builder-id="${this.builderId}"
        data-brand-name="${this.brandName}"
        data-community-name="${this.communityName}"
        data-marketing-status-label="${this.communityStatus}"
         ${this.isFeaturedListing ? `data-is-featured-listing = true` : ''}
        data-image-url="${this.thumbnail}"
        data-price-low="${this.priceLow}"
        data-price-high="${this.priceHigh}"
        data-city="${this.city}"
        data-state-abbreviation="${this.stateAbbreviation}"
        data-zip="${this.zip}">
        <div class="${classNames.join(' ')}">
            <div class="nhs-n1-c-card__container">
                ${this.renderPicture()}
                <div class="nhs-n1-c-card__body" data-qa="card_body">
                  <div class="nhs-n1-c-card__content">
                    <h3 class="nhs-o-typography--souvenir-subhead-2-large-light nhs-u-text--ellipsis">
                      <a href="${this.url}" target="_blank"
                        data-link
                        data-qa="community-name">${this.communityName}
                      </a>
                    </h3>
                      <div class="nhs-u-flex-column nhs-u-gap--200 nhs-u-width--full">
                        <div class="nhs-u-flex-row nhs-u-gap--200">
                          <p class="nhs-n1-c-card__price" data-card-element="price">
                            <span data-qa="price_label"> ${this.priceText} </span>
                          </p>
                        </div>
                        <ul class="nhs-n1-c-card__stats">
                          ${this.bedrooms ? `
                            <li class="nhs-n1-c-card__stats-stat">
                              <div class="nhs-n1-c-card__stats-stat-icon-box">
                                <svg class="nhs-n1-c-card__stats-stat-icon-svg" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                                <path fill="currentColor" fill-rule="evenodd"
                                    d="M9.5 3c-.597 0-1.134.263-1.5.678A1.995 1.995 0 0 0 6.5 3h-1a2 2 0 0 0-2 2v1H3a1.5 1.5 0 0 0-1.5 1.5v5a.5.5 0 0 0 1 0V10h11v2.5a.5.5 0 0 0 1 0v-5A1.5 1.5 0 0 0 13 6h-.5V5a2 2 0 0 0-2-2h-1Zm4 6V7.5A.5.5 0 0 0 13 7H3a.5.5 0 0 0-.5.5V9h11Zm-5-3V5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1h-3Zm-1-1v1h-3V5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1Z"
                                    clip-rule="evenodd"></path>
                                </svg>
                              </div>
                              <span>
                                <span class="nhs-n1-c-card__stats-stat-count">${this.bedrooms}</span>
                                ${this.bedrooms === '1'  ? 'Bed' : 'Beds'}
                              </span>
                            </li>
                            ` : ''}
                            ${this.bathrooms ? `
                            <li class="nhs-n1-c-card__stats-stat">
                              <div class="nhs-n1-c-card__stats-stat-icon-box">
                                <svg class="nhs-n1-c-card__stats-stat-icon-svg" xmlns="http://www.w3.org/2000/svg" width="16"
                                height="16" fill="none" viewBox="0 0 16 16">
                                  <path fill="currentColor" fill-rule="evenodd"
                                      d="M5.25.5A2.75 2.75 0 0 0 2.5 3.25V8h-2a.5.5 0 0 0 0 1H2v2a2 2 0 0 0 2 2h.5v2h1v-2h5v2h1v-2h.5a2 2 0 0 0 2-2V9h1.5a.5.5 0 0 0 0-1h-12V3.25A1.75 1.75 0 0 1 6.982 3H5v1h5V3H7.989A2.75 2.75 0 0 0 5.25.5ZM3 11V9h10v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Zm3-6H5v1h1V5Zm1 0h1v1H7V5Zm3 0H9v1h1V5Z"
                                      clip-rule="evenodd">
                                  </path>
                                </svg>
                              </div>
                              <span>
                                <span class="nhs-n1-c-card__stats-stat-count">${this.bathrooms}</span>
                                ${this.bathrooms === '1' ? 'Bath' : 'Baths'}
                              </span>
                            </li>
                            ` : ''}
                            ${this.squareFeet ? `
                              <li class="nhs-n1-c-card__stats-stat">
                                <div class="nhs-n1-c-card__stats-stat-icon-box">
                                  <svg class="nhs-n1-c-card__stats-stat-icon-svg" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
                                    <path fill="currentColor" fill-rule="evenodd"
                                        d="M2.5 2a.5.5 0 0 1 .854-.353l11 11a.5.5 0 0 1-.354.854H3a.5.5 0 0 1-.5-.5V2Zm1 10.5h9.293L3.5 3.209V12.5Zm1.426-5a.5.5 0 0 1 .852-.354l3.209 3.18a.5.5 0 0 1-.352.855H5.426a.5.5 0 0 1-.5-.5v-3.18Zm1 2.681H7.42l-1.494-1.48v1.48Z"
                                        clip-rule="evenodd">
                                    </path>
                                  </svg>
                                </div>
                                <span>
                                  <span class="nhs-n1-c-card__stats-stat-count">${this.squareFeet}</span>
                                  <abbr title="Square feet">SqFt</abbr>
                                </span>
                              </li>
                            ` : ''}
                            <li class="nhs-n1-c-card__stats-stat nhs-u-width--full">
                              <div class="nhs-n1-c-card__stats-stat-icon-box">
                                <svg 
                                  class="nhs-n1-c-card__stats-stat-icon-svg"
                                  width="16" height="16" 
                                  viewBox="0 0 16 16" 
                                  fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path d="M6.27686 2.5014C6.38352 2.50001 6.49029 2.53224 6.58057 2.59906L9.82178 4.99945L10.9155 5.80902C10.917 5.81009 10.918 5.81183 10.9194 5.81293L11.981 6.59906L12.0562 6.66644C12.2116 6.83952 12.2293 7.10406 12.0854 7.29828C11.9415 7.492 11.6838 7.55165 11.4731 7.45355L11.3862 7.40179L11.1177 7.20258V12.4536C11.1175 13.0049 10.6716 13.4532 10.1196 13.4545C9.06007 13.4572 7.73315 13.467 6.40479 13.477C5.07706 13.487 3.74679 13.4968 2.68408 13.4995C2.12988 13.5006 1.68434 13.0511 1.68408 12.5004V7.15179L1.30322 7.44379C1.08409 7.61118 0.770841 7.56988 0.603027 7.35101C0.435242 7.13193 0.476963 6.81783 0.695801 6.64984L2.0376 5.62152L5.97412 2.60394C6.06319 2.53565 6.17026 2.50186 6.27686 2.5014ZM9.82568 2.41742C9.98208 2.30827 10.1885 2.29808 10.355 2.39105L10.4351 2.44574L14.2554 5.68109L14.2573 5.68304L15.4399 6.685C15.6503 6.86341 15.6767 7.17845 15.4985 7.3891C15.3201 7.59934 15.004 7.62586 14.7935 7.44769L14.4321 7.14105V11.9985C14.4318 12.5504 13.9841 12.9984 13.4321 12.9985H12.5005V11.9985H13.4321V6.29437L13.1577 6.06293L10.145 3.51117L9.7749 3.89398L9.00635 3.25238L9.75244 2.47992L9.82568 2.41742ZM2.68408 6.38617V12.4985C3.36859 12.4968 4.16481 12.4903 5.00049 12.4848V8.33148C5.00073 8.05554 5.22449 7.83148 5.50049 7.83148H7.50049C7.77644 7.83153 8.00025 8.05557 8.00049 8.33148V12.4672C8.76275 12.4621 9.48722 12.4561 10.1177 12.4545V6.46234L6.28467 3.62738L2.68408 6.38617ZM6.00049 12.3315H7.00049V8.83148H6.00049V12.3315Z" fill="#326C71"/>
                                </svg>
                              </div>
                              <span> 
                                <abbr class="nhs-u-text--ellipsis" title="${this.communityLabel}">${this.communityLabel}</abbr> in <span class="nhs-n1-c-card__info" data-qa="tooltip-address"> ${this.address} </span>
                              </span>
                            </li>
                        </ul>
                        ${this.brandName ? `
                          <div class="nhs-n1-c-card__info nhs-u-flex-row nhs-u-gap--200" data-qa="listing_brand">
                            By ${this.brandName}
                          </div>
                        ` : ''}
                      </div>
                    </div>
                </div>
                ${this.getCardButton()}
            </div>
        </div>
      </div>
  `;
  }
}